/**
 * 🔐 传输数据结构
 */
export interface LzdHybridPayload {
  /** Base64 编码的 RSA 的公钥  */
  ak?: string;
  /** Base64 编码的 RSA 加密后的 AES 密钥 */
  sk: string;
  /** Base64 编码的初始化向量 */
  iv: string;
  /** Base64 编码的 AES 加密数据 */
  body: string;
}

/**
 * Base64 转 ArrayBuffer
 */
export function toArrayBuffer(base64Str: string): ArrayBuffer {
  const binary = globalThis.atob(base64Str);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * ArrayBuffer 转 Base64
 */
export function fromArrayBuffer(buffer: ArrayBuffer): string {
  return globalThis.btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

/**
 * 🔐
 * 混合加密/解密类
 */
export class LzdHybridEncrypt {
  private rsaPublicKey: CryptoKey | null = null;

  constructor(private rsaPublicKeyBase64: string) {}

  // 初始化：导入 RSA 公钥
  async init(): Promise<void> {
    const keyData = toArrayBuffer(this.rsaPublicKeyBase64);
    this.rsaPublicKey = await globalThis.crypto.subtle.importKey(
      'spki',
      keyData,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256'
      },
      false,
      ['encrypt']
    );
  }

  // 生成 AES 密钥
  async generateAesKey(): Promise<CryptoKey> {
    if (!this.rsaPublicKey) throw new Error('RSA 公钥未初始化');

    return await globalThis.crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 256
      },
      true,
      ['encrypt', 'decrypt']
    );
  }

  // 导出 AES 密钥为原始字节
  async exportAesKeyRaw(aesKey: CryptoKey): Promise<ArrayBuffer> {
    return await globalThis.crypto.subtle.exportKey('raw', aesKey);
  }

  // 使用 RSA 加密 AES 密钥
  async encryptAesKey(aesKeyRaw: ArrayBuffer): Promise<ArrayBuffer> {
    if (!this.rsaPublicKey) throw new Error('RSA 公钥未初始化');

    return await globalThis.crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP'
      },
      this.rsaPublicKey,
      aesKeyRaw
    );
  }

  // 使用 AES 加密 JSON 数据
  async encryptAesData(
    aesKey: CryptoKey,
    data: unknown
  ): Promise<{ iv: Uint8Array<ArrayBuffer>; ciphertext: ArrayBuffer }> {
    const encoder = new TextEncoder();
    const jsonData = JSON.stringify(data);
    const dataBuffer = encoder.encode(jsonData);

    // 生成随机 IV（12 字节用于 GCM）
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await globalThis.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: 128 // 必须指定
      },
      aesKey,
      dataBuffer
    );

    return { iv, ciphertext };
  }

  // 执行混合加密
  async encrypt(data: unknown): Promise<LzdHybridPayload> {
    // 1. 生成 AES 密钥
    const aesKey = await this.generateAesKey();

    // 2. 导出 AES 密钥并用 RSA 加密
    const aesKeyRaw = await this.exportAesKeyRaw(aesKey);
    const encryptedAesKeyBuffer = await this.encryptAesKey(aesKeyRaw);
    const encryptedAesKey = fromArrayBuffer(encryptedAesKeyBuffer);

    // 3. 使用 AES 加密数据
    const { iv, ciphertext } = await this.encryptAesData(aesKey, data);
    const ivBase64 = fromArrayBuffer(iv.buffer);
    const ciphertextBase64 = fromArrayBuffer(ciphertext);

    // 4. 返回传输结构
    return {
      sk: encryptedAesKey,
      iv: ivBase64,
      body: ciphertextBase64
    };
  }
}

/**
 * 🔐 加密数据
 */
export async function encrypt(data: unknown, rsaPublicKeyBase64: string) {
  const encryptor = new LzdHybridEncrypt(rsaPublicKeyBase64);
  // 初始化 RSA 公钥
  await encryptor.init();
  return encryptor.encrypt(data);
}

/**
 * 解密
 */
export async function decrypt<T>(
  data: LzdHybridPayload,
  privateKeyBase64: string
) {
  const rsaPrivateKeyData = toArrayBuffer(privateKeyBase64);
  const rsaPrivateKey = await globalThis.crypto.subtle.importKey(
    'pkcs8',
    rsaPrivateKeyData,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256'
    },
    false,
    ['decrypt']
  );

  const encryptedAesKeyData = toArrayBuffer(data.sk);
  const encryptedAesIvData = toArrayBuffer(data.iv);
  const decryptedAesKeyBuffer = await globalThis.crypto.subtle.decrypt(
    { name: 'RSA-OAEP' },
    rsaPrivateKey,
    encryptedAesKeyData
  );

  const aesKey = await crypto.subtle.importKey(
    'raw',
    decryptedAesKeyBuffer,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );

  const bodyBuffer = toArrayBuffer(data.body);
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: encryptedAesIvData },
    aesKey,
    bodyBuffer
  );

  const result = new TextDecoder().decode(decrypted);
  return JSON.parse(result) as T;
}

export async function encryptXXX() {
  // 得到AES 密钥
  const aesKey = await crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256
    },
    true,
    ['encrypt', 'decrypt']
  );

  const encoder = new TextEncoder();
  const data = encoder.encode('6666');

  // 12 字节 IV（GCM 推荐）
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // 加密数据
  const ciphertextBuffer = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv,
      tagLength: 128 // 必须指定
    },
    aesKey,
    data
  );

  const ciphertext = fromArrayBuffer(ciphertextBuffer);
  const ivBase64 = fromArrayBuffer(iv.buffer);

  // 提取原始密钥用于模拟传输（实际应使用 RSA 加密）
  const rawKeyBuffer = await crypto.subtle.exportKey('raw', aesKey);
  const aesKeyBase64 = fromArrayBuffer(rawKeyBuffer);

  console.log('============ AES KEY =======');
  console.log(aesKeyBase64);
  console.log('============ ivBase64 =======');
  console.log(ivBase64);
  console.log('============ ciphertext =======');
  console.log(ciphertext);
  console.log('============  =======');
}

export async function encryptRsaXxxxxx() {
  const toBase64Str = (buffer: ArrayBuffer) => {
    const bytes = new Uint8Array(buffer);
    return btoa(String.fromCharCode(...bytes));
  };
  // 1. 生成 RSA-OAEP 密钥对（SHA-256）
  console.log('1. 正在生成 2048 位 RSA 密钥对...');
  const keyPair = await crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256' // ✅ 关键：指定 SHA-256
    },
    true, // 可提取
    ['encrypt', 'decrypt'] // 支持加解密
  );
  console.log('✅ 密钥对生成成功\n');

  // 2. 加密文本 "8888"
  const data = new TextEncoder().encode('8888');
  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    keyPair.publicKey,
    data
  );
  const encryptedBase64 = toBase64Str(encryptedBuffer);
  console.log(encryptedBase64);

  // 2. 导出公钥（X.509 格式）
  const publicKeyRaw = await crypto.subtle.exportKey('spki', keyPair.publicKey);
  const publicKeyBase64 = toBase64Str(publicKeyRaw);
  // 3. 导出私钥（PKCS#8 格式）
  const privateKeyBuffer = await crypto.subtle.exportKey(
    'pkcs8',
    keyPair.privateKey
  );
  const privateKeyBase64 = toBase64Str(privateKeyBuffer);

  // ✅ 输出为 JSON 格式，避免复制错误
  const result = {
    encryptedBase64,
    publicKeyBase64,
    privateKeyBase64,
    encryptedLength: encryptedBase64.length, // 应为 344
    privateKeyLength: privateKeyBase64.length // 应为 ~1724
  };

  console.log('🔒 RSA 加密结果（复制到 Java 测试）:');
  console.log(JSON.stringify(result, null, 2));
  console.log(result);
}

export async function encryptRsaXxxxxx666() {
  const toBase64Str = fromArrayBuffer;
  // 1. 生成 RSA-OAEP 密钥对（SHA-256）
  console.log('1. 正在生成 2048 位 RSA 密钥对...');
  const binaryDer = toArrayBuffer(
    'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAoALiKgpcKvv3BdPTkEEiOfTT3dEcVccRPgz1VSl+jh90uzodbr+CIeY+7w5LTweyvgShvxzOMFbDyhk3lt+F68DZsEvG1BEdhop6P5adEMazWXoRr2nsB2dbeN4+Orlq7Iu58ByJn7Ik0NkK4DPkoLifrq5J00cm8upR7LZAVJpagQUyMwDU4axg7j2bfe8sUP0AKNnxNMmIntm4mfW4FddFWzY5bvrESx+iVyyCp+ufEAXNzQxAj31LS0Km33KnmLXnsjpoiZI2VjIuKpzjmq3GzRq4ImDBIUXgcDvY4SXBJjLU7+t8TGpTwuicQIAFwnDAXih05+0tA5kxRpkiowIDAQAB'
  );
  const keyPair = await crypto.subtle.importKey(
    'spki', // X.509 格式
    binaryDer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256'
    },
    true,
    ['encrypt']
  );
  console.log('✅ 密钥对生成成功\n');

  // 2. 加密文本 "8888"
  const data = new TextEncoder().encode('');
  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    keyPair,
    data
  );
  const encryptedBase64 = toBase64Str(encryptedBuffer);
  console.log(encryptedBase64);

  // ✅ 输出为 JSON 格式，避免复制错误
  const result = {
    encryptedBase64,
    encryptedLength: encryptedBase64.length // 应为 344
  };

  console.log('🔒 RSA 加密结果（复制到 Java 测试）:');
  console.log(JSON.stringify(result, null, 2));
  console.log(result);
}

export async function encryptRsaXxxxxx777() {
  const toBase64Str = (buffer: ArrayBuffer) => {
    const bytes = new Uint8Array(buffer);
    return btoa(String.fromCharCode(...bytes));
  };
  const rsaKeyPair = await crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256' // ✅ 关键：指定 SHA-256
    },
    true, // 可提取
    ['encrypt', 'decrypt'] // 支持加解密
  );

  // 2. 导出公钥（X.509 格式）
  const rsaPublicKeyRaw = await crypto.subtle.exportKey(
    'spki',
    rsaKeyPair.publicKey
  );
  // 3. 导出私钥（PKCS#8 格式）
  const rsaPrivateKeyRaw = await crypto.subtle.exportKey(
    'pkcs8',
    rsaKeyPair.privateKey
  );

  const aesKey = await crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256
    },
    true,
    ['encrypt', 'decrypt']
  );
  const aesKeyRaw = await crypto.subtle.exportKey('raw', aesKey);

  // 12 字节 IV（GCM 推荐）
  const aesIv = crypto.getRandomValues(new Uint8Array(12));
  const aesIvRaw = aesIv.buffer;

  // 加密数据
  const aesCiphertextBuffer = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: aesIv,
      tagLength: 128 // 必须指定
    },
    aesKey,
    new TextEncoder().encode(JSON.stringify({ pks: [1, 2, 4], whrer: [] }))
  );

  const encryptedAesKeyBuffer = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    rsaKeyPair.publicKey,
    aesKeyRaw
  );

  console.log(
    JSON.stringify({
      PublicKey: toBase64Str(rsaPublicKeyRaw),
      PrivateKey: toBase64Str(rsaPrivateKeyRaw),
      AesKey: toBase64Str(encryptedAesKeyBuffer),
      AesIv: toBase64Str(aesIvRaw),
      AesBody: toBase64Str(aesCiphertextBuffer)
    })
  );
}

export async function encryptRsaXxxxxx888() {
  const toBase64Str = fromArrayBuffer;

  const keyData = toArrayBuffer(
    'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAq51Ud9A4d6eg5Gw0Wn8TpL2iyJahcV5byu6OK1ygSZgDCu6JavxbrGvbiUvD+fZQKKMTLHYZfMWjPNGCfKg0BryIBKMDlKzvyn7NjWYvnvhtQGRpS2RxRVO+4IvRoYsapnEEEiPqjB3EJfgYMCbx0NAArihasqMg5wXsVnEgWJaV1m68cl1Cag5GIQx4fRUseyb9gtP4T5Z/MLbFFKo1PthBaeVzTyoj+9kFrislAQAB66aWMvVx1nBdo6TLAXOFlzKM4XbG3Fps/y88XpjZ5pJ3aCkN+bb9+BRGtZDZkx3oy0fwk/YEyVKMMcOvVoBYQn1C8FABso8p11SMDlvSSQIDAQAB'
  );
  const rsaPublicKey = await globalThis.crypto.subtle.importKey(
    'spki',
    keyData,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256'
    },
    false,
    ['encrypt']
  );

  const aesKey = await crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256
    },
    true,
    ['encrypt', 'decrypt']
  );
  const aesKeyRaw = await crypto.subtle.exportKey('raw', aesKey);

  // 12 字节 IV（GCM 推荐）
  const aesIv = crypto.getRandomValues(new Uint8Array(12));
  const aesIvRaw = aesIv.buffer;

  // 加密数据
  const aesCiphertextBuffer = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: aesIv,
      tagLength: 128 // 必须指定
    },
    aesKey,
    new TextEncoder().encode(JSON.stringify({ pks: [1, 2, 4], whrer: [] }))
  );

  const encryptedAesKeyBuffer = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    rsaPublicKey,
    aesKeyRaw
  );

  console.log(
    JSON.stringify({
      AesKey: toBase64Str(encryptedAesKeyBuffer),
      AesIv: toBase64Str(aesIvRaw),
      AesBody: toBase64Str(aesCiphertextBuffer)
    })
  );
}
